import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    GoogleMap,
    useJsApiLoader,
    Circle,
    DrawingManager,
    Autocomplete,
} from "@react-google-maps/api";
import { Box, Skeleton } from "@mui/material";

const mapLibraries = ["drawing", "places", "geometry"];

export const DEFAULT_MAP_LAT_LNG = { lat: 37.33479, lng: -121.88814 };

const GeofenceCircleArea = ({
    center,
    setCircle,
    radius,
    zoom,
    clearClick,
    height,
}) => {
    const { isLoaded, error } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: process.env.REACT_APP_NEXT_PUBLIC_GOOGLE_MAPS_API,
        libraries: mapLibraries,
        version: "3.64",
    });

    const [map, setMap] = useState();

    const [circleData, setCircleData] = useState(
        center?.lng && center?.lat
            ? { center, radius }
            : { center: DEFAULT_MAP_LAT_LNG, radius: 0 }
    );

    useEffect(() => {
        setCircleData(
            center?.lng && center?.lat
                ? { center, radius }
                : { center: DEFAULT_MAP_LAT_LNG, radius: 0 }
        );
    }, [center, radius]);

    useEffect(() => {
        if (clearClick) {
            setCircleData({ center: undefined, radius: undefined });
        }
    }, [clearClick]);

    // get user's geo
    useEffect(() => {
        if (center) return;

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };
                map?.setCenter(pos);
                map?.setZoom(zoom);
            },
            () => {
                map?.setCenter(DEFAULT_MAP_LAT_LNG);
                map?.setZoom(2);
            }
        );
    }, [map, center, zoom]);

    const circleRef = useRef();
    const listenersRef = useRef([]);

    const onEditCircle = useCallback(() => {
        if (circleRef.current) {
            const _center = circleRef.current.getCenter();
            setCircle({
                center: { lat: _center?.lat(), lng: _center?.lng() },
                radius: circleRef.current.getRadius() | 0,
            });
        }
    }, [setCircle]);

    const onCircleLoad = useCallback(
        (circle) => {
            circleRef.current = circle;
            listenersRef.current.push(
                circle.addListener("set_at", onEditCircle),
                circle.addListener("insert_at", onEditCircle),
                circle.addListener("remove_at", onEditCircle)
            );
            if (center) {
                const map = circleRef.current.getMap();
                map?.fitBounds(circleRef.current.getBounds());
            }
        },
        [onEditCircle]
    );
    // Clean up refs
    const onCircleUnmount = useCallback(() => {
        listenersRef.current.forEach((lis) => lis.remove());
        circleRef.current = undefined;
    }, []);

    const [searchBox, setSearchBox] = useState();

    if (!isLoaded || error) {
        return <Skeleton variant="rectangular" animation="wave" height={500} />;
    }

    return (
        <Box>
            <GoogleMap
                mapContainerStyle={{ width: "100%", height: height }}
                zoom={zoom}
                center={center}
                onLoad={(map) => setMap(map)}
                options={{
                    mapTypeControl: false,
                    draggable: false,
                    scaleControl: false,
                    scrollwheel: false,
                    navigationControl: false,
                    streetViewControl: false,
                    zoomControl: false,
                    fullscreenControl: false,
                    clickableIcons: false,
                }}
            >
                {circleData?.center && circleData?.radius && (
                    <Circle
                        // Make the Polygon editable / draggable
                        //editable
                        //draggable
                        center={circleData.center}
                        radius={circleData.radius}
                        // Event used when manipulating and adding points
                        onLoad={onCircleLoad}
                        onUnmount={onCircleUnmount}
                        options={{
                            fillColor: `#009eee`,
                            //fillOpacity: 1,
                            strokeColor: "#009eee",
                            strokeWeight: 1,
                        }}
                    />
                )}
            </GoogleMap>
        </Box>
    );
};

GeofenceCircleArea.propTypes = {
    zoom: PropTypes.number,
    clearClick: PropTypes.number,
    center: PropTypes.object,
    radius: PropTypes.number,
    setCircle: PropTypes.func,
};

export default GeofenceCircleArea;
